import React, { useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Close, Delete, Clear } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { dateToUtcSeconds, utcSecondsToLocalString } from "../utils/dateTime";
import axios from "axios";

function KeyModal({
  open,
  onClose,
  selectedDevice,
  selectedDeviceName,
  keys,
  setKeys,
  newKey,
  setNewKey,
  validFrom,
  setValidFrom,
  validTo,
  setValidTo,
  token,
  showMessage,
  showConfirm,
  serverUrl,
  devices,
  setDevices,
}) {
  const { t } = useTranslation();
  const selectedDeviceInfo = (devices || []).find(
    (device) => device.id === selectedDevice
  );
  const blockReason = selectedDeviceInfo?.block_reason || null;
  const isBlocked = Boolean(selectedDeviceInfo?.is_blocked);
  const isRetryBlocked =
    isBlocked && blockReason === "too_many_retries";
  const isAdminBlocked = isBlocked && blockReason === "admin";
  const disableActions = isBlocked;

  const resetForm = () => {
    setNewKey("");
    setValidFrom(null);
    setValidTo(null);
  };

  useEffect(() => {
    if (open && selectedDevice) {
      resetForm();
      axios.get(`${serverUrl}/device/${selectedDevice}/keys`, {
        headers: { Authorization: token }
      }).then((res) => setKeys(res.data.keys))
        .catch(() => showMessage(t("loadKeysFailed")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDevice]);

  const deleteKey = (keyId) => {
    showConfirm(t("confirmDeleteTitle"), t("confirmDelete"), () => {
      axios.delete(`${serverUrl}/device/${selectedDevice}/keys/${keyId}`, {
        headers: { Authorization: token }
      }).then(() => {
        showMessage(t("deleteSuccessfull"));
        axios.get(`${serverUrl}/device/${selectedDevice}/keys`, {
          headers: { Authorization: token }
        }).then((res) => setKeys(res.data.keys));
      }).catch(() => showMessage(t("deleteFailed")));
    });
  };

  const addKey = () => {
    const sanitizedKey = newKey.trim();
    const isValidLength = sanitizedKey.length === 4 || sanitizedKey.length === 6;
    const isNumeric = /^\d+$/.test(sanitizedKey);

    if (!isValidLength || !isNumeric) {
      return showMessage(t("newKeyRequirementsError"));
    }

    const payload = {
      passkey: sanitizedKey,
      valid_from: validFrom ? dateToUtcSeconds(validFrom) : null,
      valid_to: validTo ? dateToUtcSeconds(validTo) : null,
    };
    axios.post(`${serverUrl}/device/${selectedDevice}/keys`,
      payload,
      { headers: { Authorization: token } }
    ).then(() => {
      showMessage(t("keyAddedSuccessfully"));
      resetForm();
      axios.get(`${serverUrl}/device/${selectedDevice}/keys`, {
        headers: { Authorization: token }
      }).then((res) => setKeys(res.data.keys));
    }).catch(() => showMessage(t("keyAddingFailed")));
  };

  const unlockDoor = () => {
    showConfirm(t("confirmUnlockTitle"), t("confirmUnlock"), () => {
      axios.post(`${serverUrl}/device/${selectedDevice}/unlock`, {}, {
        headers: { Authorization: token }
      }).then(() => showMessage(t("unlockedSuccessfully")))
        .catch(() => showMessage(t("unlockedFailed")));
    });
  };

  const clearRetryLockout = () => {
    axios.post(
      `${serverUrl}/device/${selectedDevice}/clear-lockout`,
      {},
      { headers: { Authorization: token } }
    )
      .then(() => {
        showMessage(t("deviceLockoutCleared"));
        if (typeof setDevices === "function") {
          setDevices((prev) =>
            prev.map((device) =>
              device.id === selectedDevice
                ? { ...device, is_blocked: 0, block_reason: null }
                : device
            )
          );
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || t("deviceLockoutClearFailed");
        showMessage(message);
      });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", width: { xs: '90vw', sm: 400 },
        maxHeight: "90vh", overflowY: "auto",
        bgcolor: "#ffffff", boxShadow: 24, p: 3, borderRadius: "12px"
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{selectedDeviceName}</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Box>

        {isAdminBlocked && (
          <Alert severity="error" sx={{ my: 1 }}>
            {t("deviceBlockedByAdminMessage")}
          </Alert>
        )}
        {isRetryBlocked && (
          <>
            <Alert severity="warning" sx={{ my: 1 }}>
              {t("deviceBlockedTooManyRetries")}
            </Alert>
            <Button
              onClick={clearRetryLockout}
              sx={{ mb: 1 }}
              fullWidth
              variant="contained"
              color="warning"
            >
              {t("clearRetryBlock")}
            </Button>
          </>
        )}
        <Button
          onClick={unlockDoor}
          sx={{ my: 1 }}
          fullWidth
          variant="outlined"
          disabled={disableActions}
        >
          {t("unlock")}
        </Button>
        <Divider sx={{ my: 1 }} />
        <Typography variant="h6">{t("storedKeys")}</Typography>

        <List>
          {keys.length === 0 ? (
            <Typography>{t("noKeys")}</Typography>
          ) : (
            keys.map((key) => (
              <ListItem key={key.id} secondaryAction={
                <IconButton edge="end" onClick={() => deleteKey(key.id)}>
                  <Delete />
                </IconButton>
              }>
                <ListItemText
                  primary={`${t("passkey")}: ${key.passkey}`}
                  secondary={`${t("valid")}: ${key.valid_from ? utcSecondsToLocalString(key.valid_from) : "N/A"} - ${key.valid_to ? utcSecondsToLocalString(key.valid_to) : "N/A"}`}
                />
              </ListItem>
            ))
          )}
        </List>

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6">{t("addNewKey")}</Typography>

        <TextField label={t("newKey")} fullWidth value={newKey} onChange={(e) => setNewKey(e.target.value)} margin="dense" />

        <DateTimePicker
          label={t("validFrom")}
          value={validFrom}
          onChange={(v) => setValidFrom(v || null)}
          renderInput={(params) => (
            <TextField {...params} fullWidth margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {validFrom && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setValidFrom(null)}><Clear /></IconButton>
                      </InputAdornment>
                    )}
                    {params.InputProps?.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        <DateTimePicker
          label={t("validTo")}
          value={validTo}
          onChange={(v) => setValidTo(v || null)}
          renderInput={(params) => (
            <TextField {...params} fullWidth margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {validTo && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setValidTo(null)}><Clear /></IconButton>
                      </InputAdornment>
                    )}
                    {params.InputProps?.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        <Box display="flex" gap={1} mt={2}>
          <Button onClick={resetForm} variant="outlined" fullWidth>{t("reset")}</Button>
          <Button onClick={addKey} variant="contained" fullWidth>{t("addKey")}</Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default KeyModal;
